import React, { useEffect, useCallback } from 'react';
import { injectedComponent, useText } from '@base/core';
import { CONFIRM_TOKEN_ACTIONS } from '../configs/constants';
import { SESSION_KEY_ERROR_CODES, SESSION_KEY_ERROR_SET } from '@base/constants/sessionKey';
import { useUrlParams } from '../hooks/useUrlParams';

const ERROR_PARAM = 'error';
const DEFAULT_PARAMS_TO_REMOVE = ['checkResult', 'attemptsLeft'];

const {
  PASSWORD_CHANGE_DATA_NOT_FOUND,
  MEMBER_ALREADY_ACTIVE,
  INVALID_LOGIN_PASSWORD,
} = SESSION_KEY_ERROR_CODES;

const createSessionKeyDialogParams = (email, action, memberId, utmSource, partnerId, error) => {
  return btoa(
    JSON.stringify({
      email,
      action,
      memberId,
      isFirstLaunch: false,
      utm_source: utmSource || '',
      partnerId: partnerId || '',
      urlError: error || '',
    }),
  );
};

export default injectedComponent(
  {
    name: 'ErrorParamDetector',
    services: ['dialog', 'notifier', 'access'],
    components: ['SessionKeyDialog'],
  },
  function ErrorParamDetector({
                                errorParamName = ERROR_PARAM,
                                errorCases = {},
                                defaultCase = null,
                                services: { dialog, notifier, access },
                                components: { SessionKeyDialog },
                              }) {
    const text = useText();
    const { removeUrlParams } = useUrlParams();
    const searchParams = new URLSearchParams(window.location.search);
    const error = searchParams.get(errorParamName);

    const removeUrlParamsWithDefaults = useCallback((paramsToRemove) => {
      const allParams = [...DEFAULT_PARAMS_TO_REMOVE, ...paramsToRemove];
      removeUrlParams(allParams);
    }, [removeUrlParams]);

    const handleInactiveAccount = useCallback(() => {
      const inactiveDialog = errorCases.inactive?.dialog || 'InactiveAccountDialog';
      dialog.open(inactiveDialog);
    }, [errorCases, dialog]);

    const resolveTextKey = useCallback((baseTextKey, action) => {
      if (action === 'signup' && access.hasAnyAccess()) {
        baseTextKey = 'dialog.session_key.error_member_already_active';
        return baseTextKey;
      }

      if (!baseTextKey || !action || baseTextKey === 'dialog.session_key.error_invalid' || action === 'signup' || access.hasAnyAccess()) {
        return baseTextKey;
      }

      const actionSpecificKey = `${baseTextKey}__${action}`;
      const actionSpecificTranslation = text(actionSpecificKey);

      return (actionSpecificTranslation && actionSpecificTranslation !== actionSpecificKey)
        ? actionSpecificKey
        : baseTextKey;
    }, [text]);

    const showNotification = useCallback((errorCase, error, action, options = {}) => {
      if (!errorCase) return;

      const textKey = resolveTextKey(errorCase.textKey, action);
      const localizedMessage = textKey ? text(textKey) : error;
      const notifierOptions = options.notifierOptions || errorCase.notifierOptions || { topPosition: true };

      notifier.push(localizedMessage, notifierOptions);
    }, [resolveTextKey, text, notifier]);

    const handlePasswordChangeDataNotFound = useCallback((action) => {
      const errorCase = errorCases[PASSWORD_CHANGE_DATA_NOT_FOUND];
      showNotification(errorCase, PASSWORD_CHANGE_DATA_NOT_FOUND, action);
      removeUrlParamsWithDefaults([errorParamName, 'reason']);

      return true;
    }, [errorCases, showNotification, removeUrlParamsWithDefaults, errorParamName]);

    const handleMemberAlreadyActive = useCallback((action) => {
      const errorCase = errorCases[MEMBER_ALREADY_ACTIVE];
      showNotification(errorCase, MEMBER_ALREADY_ACTIVE, action);
      removeUrlParamsWithDefaults([errorParamName, 'reason']);

      return true;
    }, [errorCases, showNotification, removeUrlParamsWithDefaults, errorParamName]);

    const handleSessionKeyError = useCallback((error, searchParams) => {
      const memberId = searchParams.get('memberId');
      const action = searchParams.get('action');
      const email = searchParams.get('email');

      if (!memberId || !action || !email) return false;

      const errorCase = errorCases[error];
      const textKey = resolveTextKey(errorCase?.textKey, action);
      const localizedMessage = textKey ? text(textKey) : error;

      notifier.push(localizedMessage, { type: 'error', timeless: true });
      removeUrlParamsWithDefaults([errorParamName, 'memberId', 'action', 'email', 'reason']);

      if ((action === CONFIRM_TOKEN_ACTIONS.CHANGE_PASSWORD && !access.hasAnyAccess()) ||
        (action === CONFIRM_TOKEN_ACTIONS.SIGNUP && access.hasAnyAccess())) {
        return true;
      }

      const dialogParams = createSessionKeyDialogParams(
        email,
        action,
        parseInt(memberId, 10),
        searchParams.get('utm_source'),
        searchParams.get('partner'),
        error,
      );
      dialog.open('SessionKeyDialog', dialogParams);

      return true;
    }, [errorCases, resolveTextKey, text, access, notifier, removeUrlParamsWithDefaults, errorParamName, dialog]);

    const handleChangePassword = useCallback((searchParams) => {
      const error = searchParams.get(errorParamName);
      if (error && SESSION_KEY_ERROR_SET.has(error)) {
        handleSessionKeyError(error, searchParams);
      }
    }, [errorParamName, handleSessionKeyError]);

    const handleErrorCase = useCallback((errorCase, error, action) => {
      if (error === 'inactive') {
        handleInactiveAccount();
        return;
      }

      if (typeof errorCase === 'function') {
        const result = errorCase({ dialog, notifier });
        if (result?.type === 'notification') {
          showNotification(result, error, action, { notifierOptions: result.notifierOptions });
        }
        return;
      }

      if (errorCase?.type === 'notification') {
        showNotification(errorCase, error, action);
      }
    }, [handleInactiveAccount, dialog, notifier, showNotification]);

    const processError = useCallback((action, searchParams) => {
      const error = searchParams.get(errorParamName);
      if (!error) return;

      if (SESSION_KEY_ERROR_SET.has(error)) {
        if (error === PASSWORD_CHANGE_DATA_NOT_FOUND) {
          handlePasswordChangeDataNotFound(action);
          return;
        }

        if (error === MEMBER_ALREADY_ACTIVE) {
          handleMemberAlreadyActive(action);
          return;
        }

        const memberId = searchParams.get('memberId');
        const email = searchParams.get('email');
        if (memberId && action && email) {
          handleSessionKeyError(error, searchParams);
          return;
        }
      }

      const errorCase = errorCases[error] || defaultCase;
      if (errorCase) {
        handleErrorCase(errorCase, error, action);
      }
      removeUrlParamsWithDefaults([errorParamName]);
    }, [errorCases, defaultCase, handleSessionKeyError, handlePasswordChangeDataNotFound, handleMemberAlreadyActive, handleErrorCase, removeUrlParamsWithDefaults, errorParamName]);

    useEffect(() => {
      const action = searchParams.get('action');

      if (!error) return;

      if (!access.detected) {
        return;
      }

      if (action === CONFIRM_TOKEN_ACTIONS.CHANGE_PASSWORD) {
        handleChangePassword(searchParams);
      }

      processError(action, searchParams);
    }, [error, access.detected, handleChangePassword, processError, searchParams]);

    return null;
  },
);
